/**
 * `#intelMethod`: the methodology panel, design §7: "generated from the registry row,
 * the run's `inputs` and `intel_config`, so it cannot drift".
 *
 * UC1's and UC2's launch rounds (their §4) put the method itself first: the definition's numbered
 * steps as an ordered list and its limitations paragraph, **rendered exactly as served**.
 * Those strings are the client's own text, already localised server-side, so nothing here
 * rewords, joins or trims them — a reader comparing the panel with the signed spec must
 * find the same sentences. Then `#uc1Terciles`, the thresholds the current run actually
 * used (per constraint, metric and country, plus the absolute limits from config), and
 * finally the datasets/indicators/run block every use case has had.
 *
 * The list's id is the use case's own (`#uc1Steps`, `#uc2Steps`), since both panes point
 * their acceptance tests at it. Steps and thresholds render only when the payload carries
 * them, so UC3 — which serves neither — is unchanged.
 *
 * **UC2 stops after the limitations paragraph** (fix wave I). Its panel is not behind a
 * disclosure — the eight sentences sit open at the foot of a one-column pane, read by
 * every reader rather than by an operator who pressed "How was this generated?" — and the
 * client's §4 is those eight sentences and that paragraph, nothing else. The indicators
 * list, the last-run timestamp and the config hash are operator detail; printed at the end
 * of a reader's column they say "this is a machine's output" about text written to say the
 * opposite. UC1 keeps all three. (UC3 no longer renders this panel: NOTES #755.)
 *
 * Fetched only while the panel is open (`enabled`): a reader who never presses "How was
 * this generated?" costs the pane nothing.
 */
import { useTranslation } from 'react-i18next';

import { fmtNum } from '../../lib/format';
import { useIntelMethodology } from './api';

/** One row of `#uc1Terciles`. */
interface TercileRow {
  key: string;
  dimension: string;
  metric: string;
  country: string;
  boundaries: [number, number] | 'programme';
}

export function Methodology({ useCase, open }: { useCase: string; open: boolean }) {
  const { t } = useTranslation();
  const { data } = useIntelMethodology(useCase, open);

  if (!open) return null;

  const steps = data?.steps ?? [];
  const terciles = data?.terciles ?? {};
  const config = (data?.config ?? {}) as Record<string, unknown>;

  const tercileRows: TercileRow[] = Object.entries(terciles).flatMap(([dimension, metrics]) =>
    Object.entries(metrics).flatMap(([metric, countries]) =>
      Object.entries(countries).map(([country, boundaries]) => ({
        key: `${dimension}-${metric}-${country}`,
        dimension,
        metric,
        country,
        boundaries,
      })),
    ),
  );

  const num = (v: unknown): string => (typeof v === 'number' ? fmtNum(v, 1) : '-');
  const absolutes: { key: string; text: string }[] = [];
  if (config.ag_share_min != null) {
    absolutes.push({
      key: 'ag_share_min',
      text: t('intel.uc1.method.absAgShare', { value: num(config.ag_share_min) }),
    });
  }
  if (config.ph_min != null && config.ph_max != null) {
    absolutes.push({
      key: 'ph',
      text: t('intel.uc1.method.absPh', { min: num(config.ph_min), max: num(config.ph_max) }),
    });
  }
  if (config.rain_anom_max != null) {
    absolutes.push({
      key: 'rain_anom_max',
      text: t('intel.uc1.method.absRainAnom', { value: num(config.rain_anom_max) }),
    });
  }

  /**
   * A country with fewer than three watersheds was scored against the **programme-wide**
   * boundaries, so its cell prints those two numbers and then says where they came from —
   * "programme-wide" on its own would leave Nigeria the only country in the table with no
   * thresholds shown, which is the opposite of what a thresholds table is for.
   */
  const boundaryText = (row: TercileRow): string => {
    if (row.boundaries !== 'programme') {
      return `${num(row.boundaries[0])} / ${num(row.boundaries[1])}`;
    }
    const wide = data?.programme_terciles?.[row.metric];
    return wide
      ? `${num(wide[0])} / ${num(wide[1])} (${t('intel.uc1.method.programmeWide')})`
      : t('intel.uc1.method.programmeWide');
  };

  return (
    <div id="intelMethod" className="intel-method result-box">
      {data ? (
        <div className="result-pad">
          {steps.length ? (
            <ol id={`${useCase}Steps`} className="uc1-steps">
              {steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          ) : (
            <p>{data.approach}</p>
          )}
          {data.limitations_text ? <p className="sub">{data.limitations_text}</p> : null}

          {useCase === 'uc2' ? null : (
            <>
            {tercileRows.length || absolutes.length ? (
              <>
                <h6 className="intel-block-title">{t('intel.uc1.method.thresholds')}</h6>
                <table id="uc1Terciles" className="uc1-terciles">
                  <thead>
                    <tr>
                      <th>{t('intel.uc1.method.thConstraint')}</th>
                      <th>{t('intel.uc1.method.thMetric')}</th>
                      <th>{t('intel.uc1.method.thCountry')}</th>
                      <th>{t('intel.uc1.method.thBoundaries')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tercileRows.map((row) => (
                      <tr key={row.key} data-testid={`uc1-tercile-${row.key}`}>
                        <td>{t(`intel.uc1.dimension.${row.dimension}`, { defaultValue: row.dimension })}</td>
                        <td>{t(`intel.uc1.metric.${row.metric}`, { defaultValue: row.metric })}</td>
                        <td>{t(`country.${row.country}`, { defaultValue: row.country })}</td>
                        <td>{boundaryText(row)}</td>
                      </tr>
                    ))}
                    {absolutes.map((row) => (
                      <tr key={row.key} data-testid={`uc1-tercile-config-${row.key}`}>
                        <td colSpan={4}>{row.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}

            <h6 className="intel-block-title">{t('intel.methodology.indicators')}</h6>
            <ul>
              {data.indicators.map((row, i) => (
                <li key={`${row.indicator}-${i}`}>
                  {row.indicator} ({row.level}, {row.period})
                </li>
              ))}
            </ul>
            {data.last_run ? (
              <p className="sub">
                {t('intel.methodology.lastRun')}: {data.last_run.finished_at ?? '-'}
              </p>
            ) : null}
            <p className="sub">
              {t('intel.methodology.configVersion')}: {data.config_version.slice(0, 12)}
            </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default Methodology;
